import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PropTypes from "prop-types";
import { taskApi } from "../../services/api";
import usePoll from "../../hooks/usePoll";
import { parseApiError } from "../../services/apiError";
import { isAgent, relativeTime } from "./userDisplay";
import UserAvatar from "../people/UserAvatar";
import { describeEvent } from "./activityText";

// The thread is small per task; follow `next` (12/page) up to a bound so
// a long-running task's history isn't cut at page one.
const MAX_PAGES = 10;

// How often an open thread asks for new events.
const LIVE_POLL_MS = 5000;

/**
 * The task's thread — comments plus the automatic status / assignment /
 * claim events — oldest first, with a comment box. This is where an agent's
 * report shows up. Owns its own fetch; the parent only passes the task id.
 */
function TaskActivity({ taskId, refreshKey = undefined, onNewEvents = undefined }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState(null);

  // Several refreshes can overlap (e.g. three files attached in a row);
  // only the newest may land, or an earlier response hides a later event.
  const requestRef = useRef(0);
  // Mirrors for the live poll (below) and for load's own diff: what the
  // thread shows now, whether a full load is in flight, and who to tell
  // about events that weren't here before.
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const fullLoadRef = useRef(false);
  const onNewEventsRef = useRef(onNewEvents);
  onNewEventsRef.current = onNewEvents;

  // A reload with a thread already on screen is quiet — no spinner, no
  // jump in the dialog — and reports the events it brought that weren't
  // shown, exactly as the poll does: a reload can be what first shows
  // someone else's "attached", and the files must re-list for it too
  // (review round, PR #328).
  const load = useCallback(async () => {
    const request = ++requestRef.current;
    const had = eventsRef.current;
    if (had.length === 0) setLoading(true);
    fullLoadRef.current = true;
    setError(null);
    try {
      const all = [];
      let page = 1;
      let count = 0;
      for (;;) {
        const { data } = await taskApi.activity(taskId, { page });
        count = data?.count ?? 0;
        all.push(...(data?.results || []));
        if (!data?.next || page >= MAX_PAGES) break;
        page += 1;
      }
      if (request !== requestRef.current) return;
      const shown = new Set(eventsRef.current.map((e) => e.id));
      setEvents(all);
      setTruncated(all.length < count);
      if (had.length > 0) {
        const added = all.filter((e) => !shown.has(e.id));
        if (added.length > 0) onNewEventsRef.current?.(added);
      }
    } catch (err) {
      if (request !== requestRef.current) return;
      setError(err);
    } finally {
      if (request === requestRef.current) {
        setLoading(false);
        fullLoadRef.current = false;
      }
    }
  }, [taskId]);

  // refreshKey changes when THIS modal did something that writes events
  // (claim/release, a file, a drawing link), so they show at once;
  // everyone else's arrive through the poll below.
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Live (projects/task-live-refresh D8): while the thread is open, ask
  // for events newer than the last one shown and append them — someone
  // else's comment, an agent's claim, a file attached elsewhere. A full
  // load in flight (or started mid-tick) wins; the append dedupes by id,
  // so a comment posted here and seen by the poll shows once.
  usePoll(
    async ({ stale }) => {
      if (fullLoadRef.current) return;
      const request = requestRef.current;
      const last = eventsRef.current.at(-1);
      const fresh = [];
      let caughtUp = false;
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const { data } = await taskApi.activity(taskId, {
          page,
          ...(last ? { since: last.created_at } : {}),
        });
        if (stale() || request !== requestRef.current) return;
        fresh.push(...(data?.results || []));
        if (!data?.next) {
          caughtUp = true;
          break;
        }
      }
      // a thread cut at MAX_PAGES has now fetched everything newer than
      // what it showed: nothing is missing any more
      if (caughtUp && last) setTruncated(false);
      const known = new Set(eventsRef.current.map((e) => e.id));
      const added = fresh.filter((e) => !known.has(e.id));
      if (added.length === 0) return;
      setEvents((prev) => {
        const ids = new Set(prev.map((e) => e.id));
        return [...prev, ...added.filter((e) => !ids.has(e.id))];
      });
      onNewEventsRef.current?.(added);
    },
    { intervalMs: LIVE_POLL_MS, resetKey: taskId },
  );

  // Not a <form>: this thread renders INSIDE TaskModal's form, and nested
  // forms are invalid HTML — the browser drops the inner one, so a comment
  // submit would fall through to "Save task". Plain button instead; Enter
  // in the textarea stays a newline.
  const handlePost = async () => {
    const text = body.trim();
    if (!text) return;
    setPosting(true);
    setPostError(null);
    try {
      const { data } = await taskApi.comment(taskId, text);
      setEvents((prev) => [...prev, data]);
      setBody("");
    } catch (err) {
      setPostError(parseApiError(err, "Failed to post comment").formError);
    } finally {
      setPosting(false);
    }
  };

  return (
    <section aria-label="Activity" className="mt-4 border-t pt-4">
      <h3 className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        Activity
      </h3>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading activity…
        </div>
      )}

      {!loading && error && (
        <Alert className="py-2">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1 text-sm">
              Couldn&apos;t load the thread.
            </span>
            <Button size="sm" variant="outline" onClick={load}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!loading && !error && events.length === 0 && (
        <div className="text-sm text-muted-foreground italic">
          No activity yet — comments and status changes will appear here.
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <ul className="task-activity mb-2 list-none p-0">
          {events.map((e) => {
            // No actor + an automatic archive = the nightly job, not a person.
            const who =
              e.actor?.username ||
              (e.verb === "archived" && e.payload?.reason === "auto"
                ? "mono"
                : "someone");
            const line = describeEvent(e);
            return (
              <li key={e.id} className="mb-2 text-sm">
                <div className="text-muted-foreground">
                  {e.actor && (
                    <UserAvatar
                      user={e.actor}
                      size="xs"
                      decorative
                      className="mr-1.5 align-text-bottom"
                    />
                  )}
                  <span className="font-semibold text-foreground">{who}</span>
                  {isAgent(e.actor) && (
                    <>
                      {" "}
                      <Badge variant="secondary" className="font-normal">
                        agent
                      </Badge>
                    </>
                  )}
                  {line && <> {line}</>}
                  {/* A real space: ml-2 is visual only, and without it the
                      line reads "...claimed itjust now". */}{" "}
                  <span className="ml-1" title={e.created_at}>
                    {relativeTime(e.created_at)}
                  </span>
                </div>
                {e.verb === "comment" && (
                  <div className="mt-1 whitespace-pre-wrap">
                    {e.payload?.body}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {truncated && (
        <div className="mb-2 text-sm text-muted-foreground">
          Showing the first {events.length} events — the newest aren&apos;t
          shown here yet.
        </div>
      )}

      <div>
        {postError && (
          <Alert variant="destructive" className="mb-2 py-2">
            <AlertDescription className="text-sm">{postError}</AlertDescription>
          </Alert>
        )}
        <Label htmlFor="task-comment" className="sr-only">
          Comment
        </Label>
        <Textarea
          id="task-comment"
          className="max-h-40"
          rows={2}
          placeholder="Add a comment — visible to everyone who can see this task"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={4000}
          disabled={posting}
        />
        <div className="mt-2 flex justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={posting || !body.trim()}
            onClick={handlePost}
          >
            {posting ? "Posting…" : "Post comment"}
          </Button>
        </div>
      </div>
    </section>
  );
}

TaskActivity.propTypes = {
  taskId: PropTypes.number.isRequired,
  refreshKey: PropTypes.string,
  // called with events the live poll brought in (someone else's work)
  onNewEvents: PropTypes.func,
};

export default TaskActivity;
